import React, { useState, useMemo, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import api from '../api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { FileCheck2, Loader2, CheckCircle2, XCircle, KeyRound } from 'lucide-react';

const validatePassword = (password) => {
  if (!password) return 'Password is required';
  if (password.length < 8) return 'Password must be at least 8 characters';
  if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter';
  if (!/[a-z]/.test(password)) return 'Password must contain at least one lowercase letter';
  if (!/\d/.test(password)) return 'Password must contain at least one number';
  if (!/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?/~`]/.test(password)) return 'Password must contain at least one special character';
  return '';
};

export const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [touched, setTouched] = useState({ password: false, confirm: false });

  // Validate token on page load
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setTokenValid(false);
        setTokenError('Invalid reset link');
        setValidating(false);
        return;
      }

      try {
        const response = await api.get(`/validate-reset-token?token=${token}`);
        if (response.data.valid) {
          setTokenValid(true);
        } else {
          setTokenValid(false);
          setTokenError(response.data.message || 'Reset link expired. Please request a new password reset.');
        }
      } catch (error) {
        setTokenValid(false);
        setTokenError('Reset link expired. Please request a new password reset.');
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const errors = useMemo(() => ({
    password: touched.password ? validatePassword(newPassword) : '',
    confirm: touched.confirm && newPassword !== confirmPassword ? 'Passwords do not match' : '',
  }), [newPassword, confirmPassword, touched]);

  const isFormValid = useMemo(() => {
    return !validatePassword(newPassword) && newPassword === confirmPassword && newPassword.length > 0;
  }, [newPassword, confirmPassword]);

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ password: true, confirm: true });
    setErrorMessage('');
    
    if (!isFormValid) return;
    
    if (!token) {
      setErrorMessage('Invalid reset link');
      return;
    }
    
    setLoading(true);

    try {
      const response = await api.post('/reset-password', { 
        token,
        new_password: newPassword 
      });
      
      if (response.data.success) {
        setSuccess(true);
        toast.success('Password reset successful!');
      } else {
        setErrorMessage(response.data.message || 'Failed to reset password');
      }
    } catch (error) {
      const errorData = error.response?.data;
      if (errorData?.message) {
        setErrorMessage(errorData.message);
      } else if (errorData?.detail) {
        if (Array.isArray(errorData.detail)) {
          setErrorMessage(errorData.detail[0]?.msg || 'Validation error');
        } else {
          setErrorMessage(errorData.detail);
        }
      } else {
        setErrorMessage('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Show loading while validating token
  if (validating) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 sm:p-8 bg-slate-50" data-testid="reset-password-page">
        <Card className="w-full max-w-md p-8 sm:p-10 bg-white rounded-2xl shadow-xl shadow-slate-200/50 border-0 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-500">Validating reset link...</p>
        </Card>
      </div>
    );
  }

  // Show error if token is invalid or expired
  if (!tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 sm:p-8 bg-slate-50" data-testid="reset-password-page">
        <Card className="w-full max-w-md p-8 sm:p-10 bg-white rounded-2xl shadow-xl shadow-slate-200/50 border-0 text-center">
          <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <XCircle className="h-8 w-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Invalid Reset Link</h2>
          <p className="text-slate-500 mb-6">
            {tokenError}
          </p>
          <Link
            to="/forgot-password"
            className="text-blue-600 hover:text-blue-700 font-semibold hover:underline transition-colors"
          >
            Request a new reset link
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 sm:p-8 bg-slate-50" data-testid="reset-password-page">
      <Card className="w-full max-w-md p-8 sm:p-10 bg-white rounded-2xl shadow-xl shadow-slate-200/50 border-0">
        {/* Logo and Title */}
        <div className="mb-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="relative p-2.5 bg-gradient-to-br from-blue-600 via-blue-600 to-indigo-600 rounded-xl shadow-lg shadow-blue-600/30">
              <FileCheck2 className="h-7 w-7 text-white" />
              <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-white/20 to-transparent" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900 tracking-tight">
              TestGenie AI
            </h1>
          </div>
          <p className="text-slate-500 text-sm sm:text-base">
            {success ? 'Password reset successful' : 'Create your new password'}
          </p>
        </div>

        {success ? (
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 className="h-8 w-8 text-emerald-600" />
            </div>
            <p className="text-slate-600 mb-6">
              Password reset successful. Please login with your new password.
            </p>
            <Button
              onClick={() => navigate('/login')}
              className="w-full h-12 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 hover:from-slate-800 hover:via-slate-700 hover:to-slate-800 text-white font-semibold rounded-xl shadow-lg shadow-slate-900/20 hover:shadow-xl hover:shadow-slate-900/30 transition-all duration-300 active:scale-[0.98]"
            >
              Go to Login
            </Button>
          </div>
        ) : (
          <>
            {errorMessage && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{errorMessage}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label 
                  htmlFor="newPassword" 
                  className="text-sm font-medium text-slate-700"
                >
                  New Password
                </Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  onBlur={() => handleBlur('password')}
                  placeholder="Enter new password"
                  className={`h-12 px-4 rounded-lg transition-all placeholder:text-slate-400 ${
                    errors.password 
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' 
                      : 'border-slate-200 focus:border-blue-500 focus:ring-blue-500/20'
                  } focus:ring-4`}
                  data-testid="reset-password-new-input"
                />
                {errors.password && (
                  <p className="text-sm text-red-500 mt-1">{errors.password}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label 
                  htmlFor="confirmPassword" 
                  className="text-sm font-medium text-slate-700"
                >
                  Confirm Password
                </Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onBlur={() => handleBlur('confirm')}
                  placeholder="Confirm new password"
                  className={`h-12 px-4 rounded-lg transition-all placeholder:text-slate-400 ${
                    errors.confirm 
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' 
                      : 'border-slate-200 focus:border-blue-500 focus:ring-blue-500/20'
                  } focus:ring-4`}
                  data-testid="reset-password-confirm-input"
                />
                {errors.confirm && (
                  <p className="text-sm text-red-500 mt-1">{errors.confirm}</p>
                )}
              </div>

              <Button
                type="submit"
                disabled={loading || !isFormValid}
                className="w-full h-12 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 hover:from-slate-800 hover:via-slate-700 hover:to-slate-800 text-white font-semibold rounded-xl shadow-lg shadow-slate-900/20 hover:shadow-xl hover:shadow-slate-900/30 transition-all duration-300 active:scale-[0.98] group disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="reset-password-submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Resetting...
                  </>
                ) : (
                  <>
                    <KeyRound className="h-4 w-4 mr-2" />
                    Reset Password
                  </>
                )}
              </Button>
            </form>

            <p className="mt-6 text-center text-sm text-slate-500">
              <Link
                to="/login"
                className="text-blue-600 hover:text-blue-700 font-semibold hover:underline transition-colors"
              >
                Back to Login
              </Link>
            </p>
          </>
        )}
      </Card>
    </div>
  );
};
